import {
  Avatar,
  Box,
  Checkbox,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import PersonalIcon from '@/assets/scheduling/schedulingInPersonal.svg';
import OnlineIcon from '@/assets/scheduling/schedulingOnline.svg';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  ScheduleAppointmentType,
  SchedulePopUpInterface,
  SchedulingProps,
  SchedulingWarningsProps,
} from '../SchedulingInterface';
import { useEffect, useState } from 'react';
import { styles } from './styles';
import { AppError } from '@/utils/AppError';
import { AppointmentTypeDTO } from '@/data/DTOs/schedule/AppointmentTypeDTO';
import { useAppointmentTypeService } from '@/data/services/schedule/useLocationService';
import { LocationDTO } from '@/data/DTOs/schedule/LocationDTO';
import { useTranslation } from 'react-i18next';
import { WhatsappPopUp } from './components/WhatsappPopUp';
import { WHATSAPP_URL } from '@/constants/constants';

export const SchedulingTypeAppointmentList = ({
  setSchedule,
  schedule,
  setLoading,
  setShowWarning,
  setWarningMessage,
  setIsValid,
  internalObject,
  setInternalObject,
  setShowPopUp,
  showPopUp,
  setNextStep,
}: SchedulingProps & SchedulingWarningsProps & SchedulePopUpInterface) => {
  const { t } = useTranslation('schedule');
  const [appointmentsType, setAppointmentsType] = useState<
    AppointmentTypeDTO[]
  >([]);
  const [locationsList, setLocationsList] = useState<LocationDTO[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  const { getAppointmentTypeList, getLocationList } =
    useAppointmentTypeService();

  const message = '';

  useEffect(() => {
    fetchAppointmentType();
  }, []);

  useEffect(() => {
    const appointmentType = appointmentsType.find(
      (item) => parseInt(item.id) === schedule.appointmentTypeId
    );

    if (!appointmentType || appointmentType.appointmentType === 'Online') {
      setLocationsList([]);
      setLoadingLocations(false);
      return;
    }

    setLocationsList([]);
    setLoadingLocations(true);
    fetchLocations();
  }, [schedule.appointmentTypeId, appointmentsType]);

  useEffect(() => {
    if (!schedule.appointmentTypeId) {
      setIsValid(false);
      return;
    }

    const appointmentType = appointmentsType.find(
      (item) => parseInt(item.id) === schedule.appointmentTypeId
    );

    if (!appointmentType) return;

    setIsValid(
      appointmentType.appointmentType === 'Online' || schedule.locationId !== ''
    );
  }, [schedule.locationId, schedule.appointmentTypeId, appointmentsType]);

  useEffect(() => {}, [schedule.appointmentTypeId]);

  const fetchAppointmentType = async () => {
    setLoading(true);
    getAppointmentTypeList()
      .then((items) => {
        if (items.length <= 0) return;
        const sortedItems = items.sort(
          (a, b) => parseInt(a.id) - parseInt(b.id)
        );
        setAppointmentsType(sortedItems);
      })
      .catch((error: AppError) => {
        setShowWarning(true);
        setWarningMessage(t(error.message));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchLocations = async () => {
    setLoading(true);
    getLocationList()
      .then((items) => {
        if (items.length <= 0) return;
        const sortedItems = items.sort(
          (a, b) => parseInt(a.id) - parseInt(b.id)
        );
        setLocationsList(sortedItems);
      })
      .catch((error: AppError) => {
        setShowWarning(true);
        setWarningMessage(t(error.message));
      })
      .finally(() => {
        setLoading(false);
        setLoadingLocations(false);
      });
  };

  const appointments = (appointmentType: AppointmentTypeDTO, index: number) => {
    return (
      <ListItemButton
        sx={[
          styles.item,
          schedule.appointmentTypeId == parseInt(appointmentType.id)
            ? styles.itemFocused
            : styles.itemNotFocused,
        ]}
        key={index}
        selected={schedule.appointmentTypeId == parseInt(appointmentType.id)}
        onClick={() => {
          const newAppointmentTypeId = parseInt(appointmentType.id);
          const newAppointmentType = appointmentType.appointmentType;
          const oldAppointmentType = appointmentsType.find(
            (item) => parseInt(item.id) === schedule.appointmentTypeId
          )?.appointmentType;
        
          const shouldResetLocation =
            oldAppointmentType && oldAppointmentType !== newAppointmentType;
        
          const updatedSchedule = {
            ...schedule,
            appointmentTypeId: newAppointmentTypeId,
            locationId: newAppointmentType === 'Online' || shouldResetLocation ? '' : schedule.locationId,
          };
        
          const updatedInternalObject = {
            ...internalObject,
            scheduleType: ScheduleAppointmentType.returnString(newAppointmentTypeId),
            ...(shouldResetLocation && { address: '', addressWhatsapp: '' }),
          };
        
          setSchedule(updatedSchedule);
          setInternalObject(updatedInternalObject);
          setIsValid(newAppointmentType === 'Online');
        }}
        
      >
        <Avatar sx={styles.iconContainer}>
          {appointmentType.appointmentType == 'Online' ? (
            <Box component="img" src={OnlineIcon} />
          ) : (
            <Box component="img" alt="" src={PersonalIcon} />
          )}
        </Avatar>

        <ListItemText
          primary={
            appointmentType.appointmentType == 'Online'
              ? t('schedule_choose_appointment_type_online')
              : t('schedule_choose_appointment_type_in_person')
          }
          secondary={
            <Typography sx={styles.subtitleCard}>
              U$ {appointmentType.price.toFixed(2)}
            </Typography>
          }
          sx={styles.titleCard}
        />
      </ListItemButton>
    );
  };

  const locationItem = (location: LocationDTO, index: number) => {
    return (
      <ListItemButton
        sx={[
          styles.item,
          schedule.locationId == location.id
            ? styles.itemFocused
            : styles.itemNotFocused,
        ]}
        key={index}
        selected={schedule.locationId == location.id}
        onClick={() => {
          setSchedule({
            ...schedule,
            locationId: location.id,
          });
          setInternalObject({
            ...internalObject,
            address: location.location + ', ' + location.address,
            addressWhatsapp: location.whatsapp,
          });
        }}
      >
        <ListItemIcon>
          <Checkbox
            checked={schedule.locationId == location.id}
            icon={<RadioButtonUncheckedIcon />}
            checkedIcon={<RadioButtonCheckedIcon />}
          />
        </ListItemIcon>
        <ListItemText
          primary={location.location}
          secondary={
            <Typography sx={styles.subtitleCard}>{location.address}</Typography>
          }
          sx={styles.titleCard}
        />
      </ListItemButton>
    );
  };

  return (
    <Box sx={styles.locationList}>
      <List>
        {appointmentsType.length === 0
          ? Array.from(new Array(2)).map((_, index) => (
              <Box key={index} sx={{ position: 'relative', mb: 1 }}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={70}
                  sx={{ border: '1px solid #ccc', borderRadius: 1 }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 16,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Skeleton variant="circular" width={50} height={50} />
                  <Skeleton
                    variant="text"
                    width={120}
                    height={20}
                    sx={{ ml: 2 }}
                  />
                </Box>
              </Box>
            ))
          : appointmentsType.map((appointmentType: AppointmentTypeDTO, index) =>
              appointments(appointmentType, index)
            )}
      </List>

      {/* <Box>
        <Typography sx={styles.title}>
          {t('schedule_choose_appointment_type_title')}
        </Typography>
        <List>
          {loadingLocations && schedule.appointmentTypeId !== 2
            ? Array.from(new Array(2)).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  width="100%"
                  height={50}
                  sx={{ mb: 1, borderRadius: 1 }}
                />
              ))
            : schedule.appointmentTypeId != 0 &&
              locationsList.length > 0 &&
              locationsList.map((location: LocationDTO, index) =>
                locationItem(location, index)
              )}
        </List>
      </Box> */}
      {appointmentsType.find(
        (item) => parseInt(item.id) === schedule.appointmentTypeId
      )?.appointmentType !== 'Online' && (
        <Box>
          <Typography sx={styles.title}>
            {t('schedule_choose_appointment_type_title')}
          </Typography>
          <List>
            {loadingLocations
              ? Array.from(new Array(2)).map((_, index) => (
                  <Skeleton
                    key={index}
                    variant="rectangular"
                    width="100%"
                    height={50}
                    sx={{ mb: 1, borderRadius: 1 }}
                  />
                ))
              : locationsList.length > 0 &&
                locationsList.map((location: LocationDTO, index) =>
                  locationItem(location, index)
                )}
          </List>
        </Box>
      )}

      <WhatsappPopUp
        setShowPopUp={setShowPopUp}
        showPopUp={showPopUp}
        buttonActionArray={[
          () => {
            setShowPopUp(false);
            if (setNextStep) setNextStep(true);
          },
          () => {
            if (internalObject)
              window.open(
                WHATSAPP_URL +
                  internalObject.addressWhatsapp +
                  '?text=' +
                  message
              );
          },
        ]}
      />
    </Box>
  );
};
